import {
  AuthAccountUserTaskTaskNameChoices,
  ChangingIcon,
  handleApiRequest,
  Table,
  Typography,
  useCheckTaskStatus,
  useFetchFile,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import {
  AuthAccountOrganizationJobPositionStatusChoices,
  JobSeekerType,
  TriggerJobSuggestionWithIdMutation,
  TriggerJobSuggestionWithIdMutationVariables,
  useTriggerJobSuggestionWithIdMutation,
} from "@graphql/generated";
import { Button, useDisclosure } from "@heroui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { UserSuggestionType } from "..";
import { NameCell } from "./NameCell";
import { SuggestionDetailsModal } from "./SuggestionDetailsModal";

interface CandidatePoolTableProps {
  jobSeekers?: UserSuggestionType[];
  onAcceptSuggestion?: (jobSuggestionId: string) => void;
  loading?: boolean;
  loadingId?: string;
  jobPositionId: string;
  onTriggerJobSuggestionSuccess?: () => void;
  status?: AuthAccountOrganizationJobPositionStatusChoices;
}

const CandidatePoolTable: React.FC<CandidatePoolTableProps> = ({
  jobSeekers,
  onAcceptSuggestion,
  loading,
  loadingId,
  jobPositionId,
  onTriggerJobSuggestionSuccess,
  status,
}) => {
  const { startCheckingPromise, isPolling } = useCheckTaskStatus({
    task: AuthAccountUserTaskTaskNameChoices.ADMIN_PERFORM_JOB_SUGGESTION,
  });
  const [triggerJobSuggestion, { loading: triggerJobSuggestionLoading }] = useTriggerJobSuggestionWithIdMutation();
  const [selectedSuggestion, setSelectedSuggestion] = useState<UserSuggestionType | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const refreshCandidates = useCallback(() => {
    setTimeout(() => {
      onTriggerJobSuggestionSuccess?.();
    }, 500);
  }, [onTriggerJobSuggestionSuccess]);

  const showRefreshToast = useCallback(
    () =>
      toast
        .promise(startCheckingPromise, {
          loading: "Refreshing candidates...",
          success: "Candidates refreshed successfully",
          error: "Failed to refresh candidates. Please try again.",
        })
        .then(refreshCandidates),
    [startCheckingPromise, refreshCandidates],
  );

  useEffect(() => {
    if (jobPositionId) {
      startCheckingPromise().then(refreshCandidates);
    }
  }, [jobPositionId, startCheckingPromise, refreshCandidates]);

  const handleTriggerJobSuggestion = useCallback(
    () =>
      handleApiRequest<TriggerJobSuggestionWithIdMutation, TriggerJobSuggestionWithIdMutationVariables>({
        apiFunction: triggerJobSuggestion,
        variables: { jobPositionId },
        componentName: "CandidatePoolTable",
        actionName: "triggerJobSuggestion",
        fallbackErrorMessage: "Failed to initiate job suggestion. Please try again.",
        displaySuccessToast: false,
        onSuccess: () => {
          showRefreshToast();
        },
      }),
    [jobPositionId, triggerJobSuggestion, showRefreshToast],
  );

  const isJobDisabled = useMemo(() => {
    return status !== AuthAccountOrganizationJobPositionStatusChoices.PUBLISHED;
  }, [status]);

  const openDetailsModal = useCallback(
    (suggestion: UserSuggestionType) => {
      setSelectedSuggestion(suggestion);
      onOpen();
    },
    [onOpen],
  );

  const columns = useMemo(() => {
    return [
      {
        header: "Name",
        cell: (data: { row: { original: UserSuggestionType } }) => {
          const jobSeeker = data.row.original.user;
          return <NameCell jobSeeker={jobSeeker} />;
        },
      },
      {
        header: "Score",
        cell: (data: { row: { original: UserSuggestionType } }) => {
          const jobSeeker = data.row.original.user;
          return <Typography className="text-sm font-bold">{jobSeeker?.profile?.score || "-"}</Typography>;
        },
      },
      {
        header: "Job/Company",
        cell: (data: { row: { original: UserSuggestionType } }) => {
          const jobSeeker = data.row.original.user;
          return (
            <Typography className="text-tiny text-zinc-600 col-span-2 ">
              {jobSeeker?.workexperiences?.length
                ? `${jobSeeker?.workexperiences?.at(-1)?.jobTitle} in ${jobSeeker?.workexperiences?.at(-1)?.organization}`
                : "-"}
            </Typography>
          );
        },
      },
      {
        header: "Resume",
        cell: (data: { row: { original: UserSuggestionType } }) => {
          const jobSeeker = data.row.original.user;
          return <ResumeCell jobSeeker={jobSeeker} />;
        },
      },
      {
        header: "Actions",
        cell: (data: { row: { original: UserSuggestionType } }) => {
          const jobSuggestionId = data.row.original.suggestionId;
          const suggestion = data.row.original;
          return (
            <div className="flex gap-2">
              <Button color="primary" variant="light" onPress={() => openDetailsModal(suggestion)} size="sm">
                Details
              </Button>
              <Button
                color="success"
                variant="bordered"
                onPress={() => onAcceptSuggestion?.(jobSuggestionId)}
                isLoading={loading && loadingId === jobSuggestionId}
                isDisabled={isJobDisabled}
                size="sm"
              >
                Confirm
              </Button>
            </div>
          );
        },
      },
    ];
  }, [onAcceptSuggestion, loading, loadingId, isJobDisabled, openDetailsModal]);

  return (
    <>
      <div className="flex justify-between items-center">
        <Typography className="text-sm font-semibold">Proposed candidates:</Typography>
        <Button
          color="primary"
          variant="bordered"
          onPress={handleTriggerJobSuggestion}
          isLoading={(isPolling || triggerJobSuggestionLoading) && !isJobDisabled}
          isDisabled={isJobDisabled}
        >
          <ChangingIcon className="w-6 h-6 fill-primary-500" />
          Refresh candidates
        </Button>
      </div>
      <Table
        columns={columns}
        data={jobSeekers || []}
        bottomContentCustomComponent={<></>}
        emptyStateText="Click refresh to view available candidates"
      />

      <SuggestionDetailsModal isOpen={isOpen} onClose={onClose} suggestion={selectedSuggestion} />
    </>
  );
};

export default CandidatePoolTable;

const ResumeCell: React.FC<{ jobSeeker: JobSeekerType }> = ({ jobSeeker }) => {
  const { fetchFile: fetchUserResumeBlob } = useFetchFile({
    urlPath: jobSeeker?.resume?.file.file,
    downloadMode: true,
    userType: UserTypeEnum.ADMIN,
  });

  return (
    <>
      {jobSeeker?.resume?.file.file ? (
        <div onClick={fetchUserResumeBlob} className="text-tiny text-blue-500 cursor-pointer">
          {jobSeeker?.resume?.file.file?.split("/")?.at(-1)}
        </div>
      ) : (
        "-"
      )}
    </>
  );
};
